import { useCallback, useEffect, useRef } from 'react';

export type ResizableMap = {
  getContainer(): HTMLElement;
  resize(): void;
};

/**
 * Keeps a mapbox/maplibre canvas sized to its container.
 *
 * The map libraries only resize on window `resize`. When the container changes
 * width on its own, such as when the dashboard sidebar collapses, the canvas
 * keeps its old width and leaves a blank strip.
 *
 * Returns a `watch` function to call with the map instance once it has loaded.
 * The observer is disconnected on unmount.
 */
export const useMapResize = () => {
  const observerRef = useRef<ResizeObserver | null>(null);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return useCallback((map: ResizableMap) => {
    observerRef.current?.disconnect();
    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => map.resize());
    observer.observe(map.getContainer());
    observerRef.current = observer;
  }, []);
};
